import { AvailabilityTimeFrame } from '@kouto/types';
import {
  DatedRatesToggle,
  DimmedText,
  Flex,
} from 'components/common/styled/common-styled';
import { useGlobalDateLocale } from 'context/DateLocaleContext';
import { format, parseISO, type Locale } from 'date-fns';
import { useBrandCurrency } from 'hooks/useBrandCurrency';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { currencyFormat } from 'utils';
import {
  deriveUnitLabelKey,
  isDayBasedTimeFrame,
} from 'utils/continuousSelection';
import formatMinutes from 'utils/duration/formatMinutes';

/** One dated slice of a tier's quote, for a stay whose rate changes by date. */
export interface DatedRate {
  amount: number;
  date: string;
  pricePerUnit: number;
}

interface CartSummaryItemUnitBreakdownProps {
  durationMinutes: number;
  /** Given when the rate differs by date: one line per date instead of "n nights x rate". */
  datedRates?: DatedRate[];
  hidePrice: boolean;
  pricePerUnit: number;
  timeFrame: AvailabilityTimeFrame | null;
  units: number;
}

const formatRateDate = (date: string, locale: Locale): string =>
  format(parseISO(date), 'EEE, MMM d', { locale });

/**
 * How a continuous tier's quoted amount came about: "3 nights x $40", or, when the rate moves with the
 * date — a weekend or a holiday — one line per date so the guest sees what each night cost.
 */
const CartSummaryItemUnitBreakdown = ({
  durationMinutes,
  datedRates,
  hidePrice,
  pricePerUnit,
  timeFrame,
  units,
}: CartSummaryItemUnitBreakdownProps) => {
  const { t } = useTranslation();
  const currency = useBrandCurrency();
  const { locale } = useGlobalDateLocale();
  const [isExpanded, setIsExpanded] = useState(false);

  const handleToggleExpanded = () => setIsExpanded((expanded) => !expanded);

  if (hidePrice) return null;

  if (datedRates && datedRates.length > 0) {
    const hidden = datedRates.length - VISIBLE_DATED_RATES;
    const shown = isExpanded
      ? datedRates
      : datedRates.slice(0, VISIBLE_DATED_RATES);
    return (
      <Flex
        className="cart-summary-item-dated-rates"
        data-testid="cart-summary-item-dated-rates"
        style={{ alignItems: 'end', flexDirection: 'column' }}
      >
        {shown.map((rate) => (
          <DimmedText
            key={rate.date}
            size={12}
            className="cart-summary-item-unit-breakdown cart-summary-item-dated-rate"
            data-testid={`cart-summary-item-dated-rate-${rate.date}`}
          >
            {`${formatRateDate(rate.date, locale)} · ${currencyFormat(currency)(
              rate.pricePerUnit,
            )}`}
          </DimmedText>
        ))}
        {hidden > 0 && (
          <DatedRatesToggle
            type="button"
            aria-expanded={isExpanded}
            className="cart-summary-item-unit-breakdown__toggle"
            data-testid="cart-summary-item-dated-rates-toggle"
            onClick={handleToggleExpanded}
          >
            {isExpanded ? t('showLess') : t('moreDates', { count: hidden })}
          </DatedRatesToggle>
        )}
      </Flex>
    );
  }

  const formatCurrency = currencyFormat(currency);
  // A clock-based tier is priced per increment (30 min), so its units are increments, not hours.
  const label =
    isDayBasedTimeFrame(timeFrame) || !units
      ? `${units} ${t(deriveUnitLabelKey(timeFrame, units))} x ${formatCurrency(
          pricePerUnit,
        )}`
      : `${formatMinutes(durationMinutes, locale)} · ${formatCurrency(
          pricePerUnit,
        )} / ${formatMinutes(durationMinutes / units, locale)}`;

  return (
    <DimmedText
      size={12}
      className="cart-summary-item-unit-breakdown"
      data-testid="cart-summary-item-unit-breakdown"
    >
      {label}
    </DimmedText>
  );
};

export default CartSummaryItemUnitBreakdown;

/** How many dated rates show before the rest fold behind a "more" control. */
const VISIBLE_DATED_RATES = 3;
